import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import Swal from "sweetalert2";
import { createPatientThunk, updatePatientThunk } from "../../redux/slices/patientSlice";
import { calculateAge } from "../../utils/calculateAge";
import { getApiErrorMessage } from "../../utils/apiError";

const EMPTY = {
  patientName: "", address: "", mobileNumber: "", altMobile: "",
  age: "", gender: "", category: "Normal", email: "",
  medicalHistory: "", allergies: "", remarks: "",
  opNumber: "", autoOpNumber: true,
};

const genderOptions = [
  { value: "Male", label: "Male" },
  { value: "Female", label: "Female" },
  { value: "Other", label: "Other" },
];
const categoryOptions = [
  { value: "Normal", label: "Normal" },
  { value: "Arab", label: "Arab" },
  { value: "Custom", label: "Custom" },
];

// `onSaved` receives the saved patient (the API response merged over the submitted form, so
// name/mobile are present even when /save echoes back a thin record) — it lets a caller that
// opened this modal on top of its own, such as AppointmentModal's "Add New", pick the new
// patient up straight away. `nested` lifts the modal and its backdrop above that host modal.
const PatientModal = ({ show, onClose, editRecord, onSaved, nested = false }) => {
  const dispatch = useDispatch();
  const { list: patients } = useSelector((s) => s.patients);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(editRecord
      ? {
          ...EMPTY,
          ...editRecord,
          age: editRecord.age ?? calculateAge(editRecord.dob) ?? "",
          opNumber: editRecord.opNumber || "",
          autoOpNumber: !editRecord.opNumber,
        }
      : EMPTY);
  }, [editRecord, show]);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  const setSelect = (field) => (opt) => setForm((f) => ({ ...f, [field]: opt?.value ?? "" }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.patientName.trim() || !form.mobileNumber.trim() || !form.age || !form.gender) {
      Swal.fire("Required", "Patient Name, Mobile, Age and Gender are required.", "warning");
      return;
    }
    if (!form.autoOpNumber && !form.opNumber.trim()) {
      Swal.fire("Required", "Enter an OP number, or switch back to auto-generate.", "warning");
      return;
    }
    const name = form.patientName.trim().toLowerCase();
    const mobile = form.mobileNumber.trim();
    const isDuplicate = patients.some((p) =>
      p.id !== editRecord?.id &&
      p.patientName?.trim().toLowerCase() === name &&
      p.mobileNumber?.trim() === mobile
    );
    if (isDuplicate) {
      Swal.fire("Duplicate Patient", "A patient with the same name and mobile number already exists.", "warning");
      return;
    }
    setSaving(true);
    // Age is int? on the API and is left as "" when no date of birth was entered.
    // An empty string fails binding for a nullable int just as it does for an int —
    // only an actual null is accepted.
    const payload = {
      ...form,
      opNumber: form.autoOpNumber ? "" : form.opNumber.trim(),
      age: form.age === "" || form.age === null ? null : Number(form.age),
    };
    try {
      const saved = editRecord?.id
        ? await dispatch(updatePatientThunk({ id: editRecord.id, data: payload })).unwrap()
        : await dispatch(createPatientThunk(payload)).unwrap();
      Swal.fire({ toast: true, position: "top-end", icon: "success", title: `Patient ${editRecord ? "updated" : "added"} successfully`, showConfirmButton: false, timer: 2000 });
      if (onSaved) onSaved({ ...payload, ...(saved || {}) });
      onClose();
    } catch (err) {
      Swal.fire("Error", getApiErrorMessage(err, "Failed to save patient. Please try again."), "error");
    } finally {
      setSaving(false);
    }
  };

  if (!show) return null;

  return (
    <>
      <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={nested ? { zIndex: 1075 } : undefined}>
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header border-0 custom-modal-header">
              <div className="page-title">
                <h4>{editRecord ? "Edit Patient" : "Add Patient"}</h4>
              </div>
              <button type="button" className="close" onClick={onClose}>
                <span>×</span>
              </button>
            </div>
            <div className="modal-body custom-modal-body">
              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-lg-6 mb-3">
                    <label className="form-label">Patient Name <span className="text-danger">*</span></label>
                    <input className="form-control" value={form.patientName} onChange={set("patientName")} placeholder="Full Name" />
                  </div>
                  <div className="col-lg-6 mb-3">
                    <label className="form-label">Mobile <span className="text-danger">*</span></label>
                    <input className="form-control" value={form.mobileNumber} onChange={set("mobileNumber")} placeholder="Primary Mobile" />
                  </div>
                  <div className="col-lg-6 mb-3">
                    <label className="form-label">Alternate Mobile</label>
                    <input className="form-control" value={form.altMobile} onChange={set("altMobile")} placeholder="Alternate Mobile" />
                  </div>
                  <div className="col-lg-6 mb-3">
                    <label className="form-label">Age <span className="text-danger">*</span></label>
                    <input type="number" className="form-control" min={0} value={form.age} onChange={set("age")} placeholder="Age in years" />
                  </div>
                  <div className="col-lg-6 mb-3">
                    <label className="form-label">Gender <span className="text-danger">*</span></label>
                    <Select classNamePrefix="react-select" options={genderOptions}
                      value={genderOptions.find((o) => o.value === form.gender) || null}
                      onChange={setSelect("gender")} placeholder="Select Gender"
                      menuPosition="fixed"
                      styles={{ menu: (base) => ({ ...base, zIndex: 9999 }) }} />
                  </div>
                  <div className="col-lg-6 mb-3">
                    <label className="form-label">Patient Category</label>
                    <Select classNamePrefix="react-select" options={categoryOptions}
                      value={categoryOptions.find((o) => o.value === form.category) || null}
                      onChange={setSelect("category")} placeholder="Select Category"
                      menuPosition="fixed"
                      styles={{ menu: (base) => ({ ...base, zIndex: 9999 }) }} />
                  </div>
                  <div className="col-lg-6 mb-3">
                    <label className="form-label d-flex align-items-center justify-content-between">
                      <span>OP Number</span>
                      <span className="form-check form-check-inline mb-0">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="autoOpNumber"
                          checked={form.autoOpNumber}
                          onChange={(e) => setForm((f) => ({ ...f, autoOpNumber: e.target.checked }))}
                        />
                        <label className="form-check-label small" htmlFor="autoOpNumber">Auto-generate</label>
                      </span>
                    </label>
                    <input
                      className="form-control"
                      value={form.autoOpNumber ? "" : form.opNumber}
                      onChange={set("opNumber")}
                      placeholder={form.autoOpNumber ? "Will be generated automatically" : "e.g. 679/26"}
                      disabled={form.autoOpNumber}
                    />
                  </div>
                  <div className="col-lg-12 mb-3">
                    <label className="form-label">Address</label>
                    <input className="form-control" value={form.address} onChange={set("address")} placeholder="Address" />
                  </div>
                  <div className="col-lg-6 mb-3">
                    <label className="form-label">Email</label>
                    <input type="email" className="form-control" value={form.email} onChange={set("email")} placeholder="Email (optional)" />
                  </div>
                  <div className="col-lg-6 mb-3">
                    <label className="form-label">Medical History</label>
                    <input className="form-control" value={form.medicalHistory} onChange={set("medicalHistory")} placeholder="e.g. Diabetes, BP" />
                  </div>
                  <div className="col-lg-6 mb-3">
                    <label className="form-label">Allergies</label>
                    <input className="form-control" value={form.allergies} onChange={set("allergies")} placeholder="e.g. Penicillin" />
                  </div>
                  <div className="col-lg-6 mb-3">
                    <label className="form-label">Remarks</label>
                    <input className="form-control" value={form.remarks} onChange={set("remarks")} placeholder="Additional remarks" />
                  </div>
                </div>
                <div className="modal-footer-btn">
                  <button type="button" className="btn btn-cancel me-2" onClick={onClose}>Cancel</button>
                  <button type="submit" className="btn btn-submit" disabled={saving}>
                    {saving ? "Saving..." : editRecord ? "Save Changes" : "Add Patient"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" onClick={onClose} style={nested ? { zIndex: 1070 } : undefined} />
    </>
  );
};

export default PatientModal;
